/**
 * Dashboard Module
 * 
 * Handles the main dashboard view with status cards and auto-refresh.
 */

import { api, ServerStatus, GitInfo } from './api';

// Refresh interval (30 seconds)
const REFRESH_INTERVAL = 30_000;

let refreshTimer: number | null = null;


/**
 * Initialize dashboard and start auto-refresh.
 */
export function initDashboard(): void {
    refreshDashboard();
    startAutoRefresh();
}

/**
 * Stop auto-refresh (when navigating away).
 */
export function stopDashboard(): void {
    if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
    }
}

/**
 * Force refresh dashboard data.
 */
export async function refreshDashboard(): Promise<void> {
    try {
        const [status, git] = await Promise.all([
            api.getStatus(),
            api.getGitStatus(),
        ]);

        updateStatusCards(status);
        updateFeatureFlags(status.features);
        updateGitInfo(git);
        updateLastUpdated();

        // Update global status badge
        updateServerStatusBadge('healthy');
    } catch (err) {
        console.error('Dashboard refresh failed:', err);
        updateServerStatusBadge('error');
    }
}

function startAutoRefresh(): void {
    if (refreshTimer) return;
    refreshTimer = window.setInterval(refreshDashboard, REFRESH_INTERVAL);
}

// ============================================================================
// UI Updates
// ============================================================================

function updateStatusCards(status: ServerStatus): void {
    // Uptime
    const uptimeEl = document.getElementById('stat-uptime');
    if (uptimeEl) {
        uptimeEl.textContent = formatUptime(status.uptime_seconds);
    }

    // Sidebar uptime
    const sidebarUptime = document.getElementById('uptime-text');
    if (sidebarUptime) {
        sidebarUptime.textContent = `Uptime: ${formatUptime(status.uptime_seconds)}`;
    }

    // Memory
    const memoryEl = document.getElementById('stat-memory');
    if (memoryEl) {
        memoryEl.textContent = `${status.memory_mb.toFixed(0)} MB`;
    }

    // CPU
    const cpuEl = document.getElementById('stat-cpu');
    if (cpuEl) {
        cpuEl.textContent = `${status.cpu_percent.toFixed(1)}%`;
    }

    // Sessions
    const sessionsEl = document.getElementById('stat-sessions');
    if (sessionsEl) {
        sessionsEl.textContent = status.active_sessions.toString();
    }

    // Version in sidebar
    const versionEl = document.getElementById('version-text');
    if (versionEl) {
        versionEl.textContent = `v${status.version}`;
    }
}

function updateFeatureFlags(features: Record<string, boolean>): void {
    const container = document.getElementById('feature-flags');
    if (!container) return;

    container.innerHTML = '';

    for (const [name, enabled] of Object.entries(features)) {
        const tag = document.createElement('span');
        tag.className = `flag-tag ${enabled ? 'enabled' : 'disabled'}`;
        tag.innerHTML = `
      <span>${enabled ? '✓' : '✗'}</span>
      <span>${formatFlagName(name)}</span>
    `;
        container.appendChild(tag);
    }
}

function updateGitInfo(git: GitInfo): void {
    const branchEl = document.getElementById('git-branch');
    if (branchEl) {
        branchEl.textContent = git.branch;
    }

    const commitEl = document.getElementById('git-commit');
    if (commitEl) {
        commitEl.textContent = git.commit_hash.slice(0, 8);
    }

    const messageEl = document.getElementById('git-message');
    if (messageEl) {
        messageEl.textContent = git.commit_message || '—';
    }

    // Update updates panel too
    const currentCommitEl = document.getElementById('current-commit');
    if (currentCommitEl) {
        currentCommitEl.textContent = git.commit_hash.slice(0, 8);
    }

    const currentMessageEl = document.getElementById('current-message');
    if (currentMessageEl) {
        currentMessageEl.textContent = git.commit_message || '—';
    }

    // Show update badge if behind
    const updateBadge = document.getElementById('update-badge');
    if (updateBadge && git.behind_remote) {
        updateBadge.classList.toggle('hidden', git.behind_remote <= 0);
    }
}

function updateLastUpdated(): void {

    const el = document.getElementById('dashboard-updated');
    if (el) {
        el.textContent = new Date().toLocaleTimeString();
    }
}

function updateServerStatusBadge(status: 'healthy' | 'warning' | 'error'): void {
    const badge = document.getElementById('server-status-badge');
    if (!badge) return;

    const dot = badge.querySelector('.status-dot');
    const text = badge.querySelector('.status-text');

    if (dot) {
        dot.className = `status-dot ${status}`;
    }

    if (text) {
        const labels = { healthy: 'Healthy', warning: 'Warning', error: 'Error' };
        text.textContent = labels[status];
    }
}

// ============================================================================
// Formatters
// ============================================================================

function formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
        return `${days}d ${hours}h`;
    } else if (hours > 0) {
        return `${hours}h ${mins}m`;
    } else {
        return `${mins}m`;
    }
}

function formatFlagName(name: string): string {
    // Convert SNAKE_CASE to Title Case
    return name
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
